import { useRef } from "react";

import { theme } from "@tm/utility/global";

import FormattedText, {
  getPlainText,
} from "../../../../components/FormattedText/FormattedText";
import Spinner from "../../../../components/Spinner/Spinner";
import cancelGreyBg from "../../../../icons/cancelGreyBg.svg";
import greenCheckImg from "../../../../icons/greenCheckImg.svg";
import type { ClaimFormField as ClaimFormFieldType } from "../../types";
import {
  Control,
  ErrorText,
  FieldRow,
  FileChip,
  FileList,
  FileName,
  FileRemove,
  HiddenFileInput,
  Input,
  InputWrap,
  LabelBlock,
  LabelTitle,
  Prefix,
  Required,
  SubLabel,
  TextArea,
  TextAreaField,
  UploadAction,
  UploadTrigger,
} from "./ClaimForm.style";
export interface ClaimFormFieldProps {
  field: ClaimFormFieldType;
  value: string;
  files: File[];
  error?: string;
  uploading?: boolean;
  disabled?: boolean;
  onValueChange: (value: string) => void;
  onFilesChange: (files: File[]) => void;
  onFilesSelected?: (files: File[]) => void;
}

function acceptFromTypes(types?: string[]): string | undefined {
  if (!types?.length) return undefined;
  return types.map((t) => `.${t.replace(/^\./, "")}`).join(",");
}

export function ClaimFormField({
  field,
  value,
  files,
  error,
  uploading = false,
  disabled = false,
  onValueChange,
  onFilesChange,
  onFilesSelected,
}: ClaimFormFieldProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fieldKey = field.key ?? "";
  const inputDisabled = disabled || uploading;

  const label = (
    <>
      <LabelTitle htmlFor={fieldKey}>
        <FormattedText value={field.label} />
        {field.isMandatory ? <Required>*</Required> : null}
      </LabelTitle>
      {getPlainText(field.subLabel).trim() ? (
        <SubLabel>
          <FormattedText value={field.subLabel} />
        </SubLabel>
      ) : null}
    </>
  );

  if (field.key === "DESCRIPTION") {
    return (
      <TextAreaField>
        <LabelTitle htmlFor={fieldKey}>
          <FormattedText value={field.label} />
          {field.isMandatory ? <Required>*</Required> : null}
        </LabelTitle>
        <TextArea
          id={fieldKey}
          placeholder={field.placeholder}
          value={value}
          disabled={inputDisabled}
          onChange={(e) => onValueChange(e.target.value)}
        />
        {error ? <ErrorText>{error}</ErrorText> : null}
      </TextAreaField>
    );
  }

  if (field.type === "fileUploader") {
    const ctaText =
      getPlainText(field.fileUpload?.ctaText).trim() || field.placeholder || "";
    const accept = acceptFromTypes(field.fileUpload?.allowedFileTypes);
    return (
      <div>
        <FieldRow>
          <LabelBlock>{label}</LabelBlock>
          <UploadAction>
            {uploading ? (
              <Spinner
                spinnerWidth={14}
                spinnerColor={theme.blueText}
                thickness={2}
              />
            ) : null}
            <UploadTrigger
              type="button"
              disabled={inputDisabled}
              onClick={() => fileInputRef.current?.click()}
            >
              {ctaText}
            </UploadTrigger>
          </UploadAction>
          <HiddenFileInput
            ref={fileInputRef}
            id={fieldKey}
            type="file"
            multiple
            accept={accept}
            disabled={inputDisabled}
            onChange={(e) => {
              const picked = Array.from(e.target.files ?? []);
              if (!picked.length) return;
              if (onFilesSelected) onFilesSelected(picked);
              else onFilesChange([...files, ...picked]);
              e.target.value = "";
            }}
          />
        </FieldRow>
        {files.length > 0 && (
          <FileList>
            {files.map((file, idx) => (
              <FileChip key={`${file.name}-${idx}`}>
                <img src={greenCheckImg.src} alt="" aria-hidden />
                <FileName title={file.name}>{file.name}</FileName>
                <FileRemove
                  type="button"
                  disabled={inputDisabled}
                  aria-label={`Remove ${file.name}`}
                  onClick={() =>
                    onFilesChange(files.filter((_, i) => i !== idx))
                  }
                >
                  <img src={cancelGreyBg.src} alt="" />
                </FileRemove>
              </FileChip>
            ))}
          </FileList>
        )}
        {error ? <ErrorText>{error}</ErrorText> : null}
      </div>
    );
  }

  const isDate = field.type === "datePicker";
  return (
    <FieldRow>
      <LabelBlock>{label}</LabelBlock>
      <Control>
        <InputWrap $hasError={Boolean(error)} $disabled={inputDisabled}>
          {field.prefix ? <Prefix>{field.prefix}</Prefix> : null}
          <Input
            id={fieldKey}
            type={isDate ? "date" : "text"}
            placeholder={field.placeholder}
            value={value}
            disabled={inputDisabled}
            readOnly={inputDisabled}
            min={isDate ? field.validation?.minDate?.value : undefined}
            max={isDate ? field.validation?.maxDate?.value : undefined}
            onChange={(e) => onValueChange(e.target.value)}
          />
        </InputWrap>
        {error ? <ErrorText>{error}</ErrorText> : null}
      </Control>
    </FieldRow>
  );
}
